import { useState } from "react";
import { Button, Card, Col, Form, Image, Input, Radio } from "antd";
import FullPageLayout from "../../../../templates/FullPageLayout";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ConstructionIcon,
  CrossIcon,
  CustomIcon,
  EnhancementsIcon,
  HolidayLightingIcon,
  IrrigationIcon,
  LandscapeIcon,
  LawnCareIcon,
  SnowServiceIcon,
  TickIcon,
  TreeServiceIcon,
} from "../../../../atoms/CustomIcons";
import { createServiceLine } from "../ducks/services";
import { AxiosError, AxiosResponse } from "axios";
import SuccessMessage from "../../../../atoms/Toasts/SuccessMessage";
import ErrorMessage from "../../../../atoms/Toasts/ErrorMessage";

const { Meta } = Card;

interface FormValues {
  name: string;
  address: string;
  crews_assigned: string;
  cont_person_first_name: string;
  cont_person_last_name: string;
  cont_person_email: string;
  cont_person_phone: string;
  selectedServiceLineDescription?: string; // Add this to the FormValues interface
}

interface ServiceLine {
  title: string;
  description: string;
  avatar: React.ReactNode;
  iconName: string;
  iconColor: string;
}

export default function CreateServiceLine() {
  const { handleSubmit } = useForm<FormValues>();
  const [serviceLineTitle, setServiceLineTitle] = useState<string | null>(null);
  const url = useLocation();
  const companyId = url?.pathname.split("/")[3];
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedTitle, setEditedTitle] = useState("");
  const [localServiceLines, setLocalServiceLines] = useState<ServiceLine[]>(
    () => [
      {
        title: "Landscape maintenance",
        description:
          "Routine upkeep of lawns, plants, beds, and hardscapes, including mowing, edging, weeding, and debris removal.",
        avatar: <LandscapeIcon className="defaultColor" />,
        iconName: "LandscapeIcon",
        iconColor: "mint_color",
      },
      {
        title: "Enhancements",
        description:
          "Landscape upgrades, including plant installations, bed renovations, seasonal color additions, and lighting improvements.",
        avatar: <EnhancementsIcon className="defaultColor" />,
        iconName: "EnhancementsIcon",
        iconColor: "red_color",
      },
      {
        title: "Lawn Care",
        description:
          "Application of fertilizers, weed control, and pesticides to maintain healthy turf and ornamental plants.",
        avatar: <LawnCareIcon className="defaultColor" />,
        iconName: "LawnCareIcon",
        iconColor: "orange_color",
      },
      {
        title: "Irrigation",
        description:
          "Installation, maintenance, and repair of irrigation systems.",
        avatar: <IrrigationIcon className="defaultColor" />,
        iconName: "IrrigationIcon",
        iconColor: "violet_color",
      },
      {
        title: "Tree services",
        description:
          "Care for trees, including pruning, removal, health assessments, and stump grinding.",
        avatar: <TreeServiceIcon className="defaultColor" />,
        iconName: "TreeServiceIcon",
        iconColor: "green_color",
      },
      {
        title: "Construction",
        description:
          "Installation of hardscape and softscape features, including patios, walkways, retaining walls, plants, and other features.",
        avatar: <ConstructionIcon className="defaultColor" />,
        iconName: "ConstructionIcon",
        iconColor: "yellow_color",
      },
      {
        title: "Holiday lighting & decor",
        description:
          "Installation, maintenance, and removal of lighting and decorations.",
        avatar: <HolidayLightingIcon className="defaultColor" />,
        iconName: "HolidayLightingIcon",
        iconColor: "blue_color",
      },
      {
        title: "Snow services",
        description:
          "Snow removal, salting, and de-icing for driveways, walkways, and parking lots.",
        avatar: <SnowServiceIcon className="defaultColor" />,
        iconName: "SnowServiceIcon",
        iconColor: "purple_color",
      },
    ]
  );

  const onSubmit = () => {
    setLoading(true);

    if (serviceLineTitle === "custom") {
      navigate(`/company-settings/custom-service/${companyId}`);
      return;
    }

    const selectedService = localServiceLines.find(
      (service) => service.title === serviceLineTitle
    );

    const payload = {
      name: serviceLineTitle ?? "",
      description: selectedService?.description ?? "",
      company_id: companyId,
      icon: selectedService?.iconName,
      color: selectedService?.iconColor,
    };

    createServiceLine(payload)
      .then((res: AxiosResponse) => {
        setLoading(false);
        SuccessMessage("Service line created");
        navigate(
          `/company-settings/service-lines/${res?.data?.id}/${companyId}`
        );
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });

    console.log("payload", payload);

    setLoading(false);
  };

  // const handleEditTitle = (index: number) => {
  //   setEditingIndex(index);
  //   setEditedTitle(localServiceLines[index].title);
  // };

  const handleSaveTitle = (index: number) => {
    const updatedServiceLines = localServiceLines.map((item, i) =>
      i === index ? { ...item, title: editedTitle } : item
    );
    setLocalServiceLines(updatedServiceLines);
    setEditingIndex(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  return (
    <FullPageLayout loading={loading}>
      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <Col style={{ width: "672px", paddingBottom: 85 }}>
          <div className="title">
            <h2 style={{ marginBottom: 8 }}>Create service line</h2>
            <p
              style={{ fontSize: "16px", lineHeight: "24px", color: "#070707" }}
            >
              A service line is a distinct segment of a landscaping business
              with its own services, crews, labor rates, and margins.
            </p>
          </div>

          <div className="service_line_card">
            <Radio.Group
              onChange={(e) => setServiceLineTitle(e.target.value)}
              value={serviceLineTitle}
            >
              {localServiceLines?.map((service, index) => (
                <Radio.Button
                  value={service?.title}
                  key={index}
                  style={{ width: "100%" }}
                  className={`icon_selected_${service?.iconColor}`}
                >
                  <Card
                    className={`service_line_card_wrapper icon_hover_${service?.iconColor}`}
                  >
                    <Meta
                      avatar={service?.avatar}
                      title={
                        editingIndex === index ? (
                          <div className="titleEditAble">
                            <div>
                              {
                                <Input
                                  value={editedTitle}
                                  onChange={(e) =>
                                    setEditedTitle(e.target.value)
                                  }
                                  onPressEnter={() => handleSaveTitle(index)}
                                  style={{
                                    border: 0,
                                    padding: 0,
                                    width: "100%",
                                  }}
                                  className="editAbleFld"
                                />
                              }
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "5px",
                              }}
                            >
                              <Button
                                size="small"
                                type="primary"
                                onClick={() => handleSaveTitle(index)}
                                icon={
                                  <TickIcon
                                    style={{ color: "rgb(21, 23, 24)" }}
                                  />
                                }
                                className="only_icon"
                              />
                              <Button
                                size="small"
                                onClick={handleCancelEdit}
                                icon={
                                  <CrossIcon
                                    style={{ color: "rgb(21, 23, 24)" }}
                                  />
                                }
                                className="only_icon"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="titleEditIcon">
                            {service?.title}
                            {/* <Button
                              size="small"
                              onClick={() => handleEditTitle(index)}
                              icon={<PencilIcon />}
                              className="only_icon"
                            /> */}
                          </div>
                        )
                      }
                      description={
                        <p
                          style={{
                            fontSize: "14px",
                            lineHeight: "20px",
                            fontWeight: "400",
                            color: "#474C4D",
                            marginBottom: "0px",
                          }}
                        >
                          {service?.description}
                        </p>
                      }
                    />

                    <div className="checkbox_image">
                      <Image
                        src="/images/services/checkbox-filled.svg"
                        alt="filled"
                        preview={false}
                        className="checkbox-filled"
                      />
                      <Image
                        src="/images/services/checkbox-unfilled.svg"
                        alt="un-filled"
                        preview={false}
                        className="checkbox-unfilled"
                      />
                    </div>
                  </Card>
                </Radio.Button>
              ))}

              <div className="title">
                <h3 style={{ paddingTop: "16px", marginBottom: 8 }}>
                  Custom service line
                </h3>
                <p
                  style={{
                    fontSize: "16px",
                    lineHeight: "24px",
                    fontWeight: "400",
                    color: "#070707",
                  }}
                >
                  If you can’t find your service line among above, you can
                  create a custom one.
                </p>
              </div>
              <Radio.Button
                value="custom"
                style={{ width: "100%" }}
                className="icon_selected_custom_icon"
              >
                <Card
                  style={{ marginBottom: 0 }}
                  className="service_line_card_wrapper icon_hover_custom_icon "
                >
                  <Meta
                    avatar={<CustomIcon className="defaultColor" />}
                    title={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          fontWeight: "700",
                        }}
                      >
                        Custom service line
                      </div>
                    }
                    description={
                      <p
                        style={{
                          fontSize: "14px",
                          lineHeight: "20px",
                          fontWeight: "400",
                          color: "#474C4D",
                          marginBottom: "0px",
                        }}
                      >
                        Create a business segment fit to your needs.
                      </p>
                    }
                  />

                  <div className="checkbox_image">
                    <Image
                      src="/images/services/checkbox-filled.svg"
                      alt="filled"
                      preview={false}
                      className="checkbox-filled"
                    />
                    <Image
                      src="/images/services/checkbox-unfilled.svg"
                      alt="un-filled"
                      preview={false}
                      className="checkbox-unfilled"
                    />
                  </div>
                </Card>
              </Radio.Button>
            </Radio.Group>
          </div>
        </Col>
        <div className="fixed_btm_card">
          <Card>
            <div className="centered">
              <Button
                htmlType="button"
                className="outlined-btn"
                onClick={() =>
                  navigate(`/company-settings/services/${companyId}`)
                }
              >
                Cancel
              </Button>
              <div style={{ display: "flex", gap: "16px" }}>
                <Button
                  htmlType="submit"
                  type="primary"
                  className="mint-btn"
                  disabled={!serviceLineTitle}
                >
                  Create service line
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </Form>
    </FullPageLayout>
  );
}
